import { JumboRadio, OnboardingStageText, useSharedTracking } from '@tlx/beehive-shared';
import { useState } from 'react';
import { SalaryOnboardingStageForm } from '@Page/Onboarding/SalaryOnboardingStage';
import { OnboardingMonthSelect, useMonths } from '@Page/Onboarding/components/OnboardingMonthSelect';
import { Month, updateSalarySettings, useSalarySettings } from '@Page/Onboarding/hooks/useSalarySettings';
import { Option } from '@tlx/atlas';
import { APIError, ValidationMessage } from '@tlx/astro-shared';
import { OnboardingStageSkeleton } from '@Page/Onboarding/components/OnboardingStageSkeleton';
import { setCustomValidityOnField } from '@Page/Onboarding/components/ValidatedForm/setCustomValidityOnField';
import { useIntl } from 'react-intl';
import { JumboRadioGroup } from '@Page/Onboarding/JumboRadioGroup';

type PaymentOfSalaryStageValues = {
    isPaidInAdvance: boolean;
    halfTaxMonth: Month;
    vacationPayMonth: Month;
    vacationMonth: Month;
    vacationCorrectionMonth: Month;
};

type PaymentOfSalaryStageProps = {
    defaultValue: PaymentOfSalaryStageValues;
    handleSubmit?: (formData: Partial<PaymentOfSalaryStageValues>) => Promise<void>;
    handleError?: (form: HTMLFormElement, error: APIError) => ValidationMessage[] | void;
};

export function ConnectedPaymentOfSalaryStage() {
    const { data, isLoading } = useSalarySettings();
    if (!data || isLoading) {
        return <OnboardingStageSkeleton />;
    }
    return (
        <PaymentOfSalaryStage
            handleSubmit={async (formData) => {
                await updateSalarySettings(formData);
            }}
            handleError={(form, error) => {
                const unhandled: ValidationMessage[] = [];
                error.details.validationMessages?.forEach((message) => {
                    if (message.field) {
                        const handled = setCustomValidityOnField(form, message.field, message.message);

                        if (!handled) {
                            unhandled.push(message);
                        }
                    }
                });

                return unhandled;
            }}
            defaultValue={data}
        />
    );
}

export function PaymentOfSalaryStage({
    handleSubmit,
    handleError,
    defaultValue: { halfTaxMonth, isPaidInAdvance, vacationCorrectionMonth, vacationMonth, vacationPayMonth },
}: PaymentOfSalaryStageProps) {
    const intl = useIntl();
    const months = useMonths();

    const [paidInAdvance, setPaidInAdvance] = useState(isPaidInAdvance);

    return (
        <SalaryOnboardingStageForm
            handleError={handleError}
            handleSubmit={handleSubmit}
            title={intl.formatMessage({
                id: 'text_salary_payments',
            })}
            description={intl.formatMessage({
                id: 'tsk_onboarding_payment_description',
            })}
        >
            <JumboRadioGroup name="isPaidInAdvance">
                <JumboRadio
                    checked={paidInAdvance}
                    value="true"
                    data-testid="isPaidInAdvance-true-radio"
                    className="atl-justify-between"
                    onClick={() => setPaidInAdvance(true)}
                >
                    <div>
                        <div className="atl-text-xl atl-font-medium">
                            {intl.formatMessage({
                                id: 'text_yes',
                            })}
                        </div>
                        <div>
                            {intl.formatMessage({
                                id: 'text_pay_in_advance',
                            })}
                        </div>
                    </div>
                </JumboRadio>
                <JumboRadio
                    checked={!paidInAdvance}
                    value="false"
                    data-testid="isPaidInAdvance-false-radio"
                    className="atl-justify-between"
                    onClick={() => setPaidInAdvance(false)}
                >
                    <div>
                        <div className="atl-text-xl atl-font-medium">
                            {intl.formatMessage({
                                id: 'text_no',
                            })}
                        </div>
                        <div>
                            {intl.formatMessage({
                                id: 'tsk_onboarding_payment_in_arrear',
                            })}
                        </div>
                    </div>
                </JumboRadio>
            </JumboRadioGroup>

            <br />
            <OnboardingStageText>
                {intl.formatMessage({
                    id: 'tsk_onboarding_payment_months_description',
                })}
            </OnboardingStageText>
            <div className="atl-flex atl-gap-8">
                <OnboardingMonthSelect
                    name="halfTaxMonth"
                    label={intl.formatMessage({
                        id: 'text_half_tax_month',
                    })}
                    defaultValue={halfTaxMonth}
                    data-testid={useSharedTracking('half-tax-dropdown')}
                >
                    <Option value={'NOT_CHOSEN'}>{months['NOT_CHOSEN']}</Option>
                    <Option value={'OCTOBER'}>{months['OCTOBER']}</Option>
                    <Option value={'NOVEMBER'}>{months['NOVEMBER']}</Option>
                    <Option value={'DECEMBER'}>{months['DECEMBER']}</Option>
                </OnboardingMonthSelect>
                <OnboardingMonthSelect
                    name="vacationPayMonth"
                    label={intl.formatMessage({
                        id: 'text_holyday_allowance_month',
                    })}
                    defaultValue={vacationPayMonth}
                    data-testid={useSharedTracking('vacation-pay-dropdown')}
                />
            </div>
            <div className="atl-flex atl-gap-8">
                <OnboardingMonthSelect
                    name="vacationMonth"
                    label={intl.formatMessage({
                        id: 'text_holyday_month',
                    })}
                    defaultValue={vacationMonth}
                    data-testid={useSharedTracking('vacation-month-dropdown')}
                />
                <OnboardingMonthSelect
                    name="vacationCorrectionMonth"
                    label={intl.formatMessage({
                        id: 'text_holyday_correction_month',
                    })}
                    defaultValue={vacationCorrectionMonth}
                    data-testid={useSharedTracking('salary-correction-dropdown')}
                />
            </div>
        </SalaryOnboardingStageForm>
    );
}
